/* eslint-disable react-hooks/exhaustive-deps */
import {
  Td,
  Tr,
  Th,
  Box,
  Text,
  Flex,
  Input,
  Table,
  Tbody,
  Radio,
  Badge,
  Thead,
  VStack,
  Button,
  HStack,
  Tooltip,
  Popover,
  useToast,
  FormLabel,
  RadioGroup,
  IconButton,
  InputGroup,
  PopoverBody,
  PopoverArrow,
  PopoverHeader,
  useDisclosure,
  PopoverContent,
  InputLeftElement,
  useColorModeValue,
  PopoverCloseButton,
} from '@chakra-ui/react';
import {
  Pagination,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
  PaginationPageGroup,
  PaginationSeparator,
  PaginationContainer,
  usePagination as useAjnaPagination
} from "@ajna/pagination";
import * as React from 'react';
import { mode } from "@chakra-ui/theme-tools";
import { ReactNode, useState, useEffect } from 'react';
import { MdLoop, MdPersonRemove, MdSend } from 'react-icons/md';
import { AddIcon, SearchIcon, WarningIcon } from '@chakra-ui/icons';
import { Column, useGlobalFilter, usePagination, useSortBy, useTable, } from 'react-table';

import Card from '../../../components/card/Card';
import TableSpinner from '../../../components/spinner/TableSpinner';
import AddMemberModal from '../../admin/modals/components/AddMemberModal';
import { PopoverTrigger } from '../../admin/dataTables/components/ClientTable';
import ResetPasswordModal from "../../admin/modals/components/ResetPasswordModal";

import {
  addMember,
  getAllMembers,
  deleteUserAsInviter,
  resetPasswordRequest,
  remindInvitationMember,
} from '../../../api/user-service';
import {  getBadgeColor } from "../../../libraries/utils";
import { COUNT_PER_PAGE } from "../../../config/constant";
import { ClientType, IMemberType } from "../../../types/user";
import { useDebounce } from "../../../libraries/useDebounce";
import { useAuth } from '../../../auth-context/auth.context';

const MEMBER_COLUMNS: Column<ClientType>[] | any = [{
    Header: 'NAME',
    accessor: 'fullName',
  }, {
    Header: 'RELATION',
    accessor: 'relationType',
  }, {
    Header: 'SOURCE',
    accessor: 'source',
  }, {
    Header: 'STATUS',
    accessor: 'accepted',
  }, {
    Header: 'VIEW DATA',
    accessor: 'options',
  }, {
    Header: 'OTHER',
    accessor: 'other',
  }];

const MemberList: React.FC = () => {
  const { user } = useAuth();

  const toast = useToast();
  const bgPagination = useColorModeValue('brand.500', 'brand.400');
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [members, setMembers] = useState<IMemberType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openedPopoverId, setOpenedPopoverId] = useState<any>(null);
  const [selectedMemberData, setSelectedMemberData] = useState<any>(null);
  const [connectType, setConnectType] = useState<'CONNECT' | 'UPLOAD'>('CONNECT');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
  const debouncedSearch = useDebounce(search, 1000);

  const {
    pages,
    offset,
    pageSize,
    pagesCount,
    currentPage,
    setCurrentPage,
  } = useAjnaPagination({
    pagesCount: Math.ceil(total / COUNT_PER_PAGE),
    limits: {
      outer: 2,
      inner: 2
    },
    initialState: {
      currentPage: 1,
      pageSize: COUNT_PER_PAGE,
    },
  });

  const tableInstance = useTable({
      columns: MEMBER_COLUMNS,
      data: members,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const nameColor = useColorModeValue('blue.600', 'white');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      const { members, count } = await getAllMembers(pageSize, offset, search);
      setMembers(members);
      setTotal(count)
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addNewMember = async ({
    email,
    lastName,
    firstName,
    middleName,
    inviteType,
    connectType,
    relationType,
  }: {
    email: string,
    lastName: string,
    firstName: string,
    inviteType: string,
    middleName: string,
    connectType: 'UPLOAD' | 'CONNECT',
    relationType: string,
  }) => {
    try {
      await addMember({
        receiverEmail: email,
        firstName,
        lastName,
        middleName,
        relationType,
        inviteType,
        connectType,
      })
      toast({
        title: 'Add Member',
        description: `${firstName} has been successfully added as a member.`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
      fetchMembers();
    } catch (error: any) {
      toast({
        title: 'Add Member',
        description: error.response?.data.message ?? error.message,
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  const removeMember = (memberId: any) => {
    setIsDeleting(true);
    deleteUserAsInviter(memberId)
      .then(() => {
        toast({
          title: 'Remove Member',
          description: `Member has been removed.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        fetchMembers();
      })
      .catch((err) => {
        toast({
          title: 'Remove Member',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsDeleting(false);
      })
  }

  const resetPassword = async ({ isAutoGenerated, password, askToChange }: {
    isAutoGenerated: boolean,
    password?: string,
    askToChange?: boolean,
  }) => {
    await resetPasswordRequest(selectedMemberData.userId, isAutoGenerated, password, askToChange)
      .then(() => {
        toast({
          title: 'Reset Password',
          description: `The password of ${selectedMemberData.firstName} ${selectedMemberData.lastName} has been reset.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((err) => {
        toast({
          title: 'Reset Password',
          description: err?.response?.data?.message || err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
  }

  const resendInvitationEmail = (userId: number, connectType?: 'CONNECT' | 'UPLOAD') => {
    remindInvitationMember(userId, connectType)
      .then(() => {
        toast({
          title: 'Email reminder has been sent.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((err) => {
        toast({
          title: 'Remind Invitation',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
  }

  useEffect(() => {
    // setSelectedMemberData(null);
    if (user) {
      fetchMembers();
    }
  }, [user, currentPage, offset, debouncedSearch]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <VStack>
        <Card
          direction="column"
          w="100%"
          px="0px"
          overflowX='auto'
          opacity={isLoading ? 0.7 : 1}
        >
          <TableSpinner loading={isLoading}/>
          <Flex px="25px" justify="space-between" mb="20px" align="center">
            <HStack>
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  zIndex={0}
                  children={<SearchIcon color="gray.300"/>}
                />
                <Input
                  type="input"
                  placeholder="Search Email or Name..."
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </HStack>
            {user && user.inviteType === 'admin' &&
              <Button
                leftIcon={<AddIcon/>}
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                minW="110px"
                ml="8px"
                onClick={onOpen}
              >
                Add Member
              </Button>
            }
          </Flex>
          <Table
            {...getTableProps()}
            variant="simple"
            color="gray.500"
            mb="24px"
            size="sm"
          >
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {column.render('Header')}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {rows.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    key={index}
                    // bgColor='#dddddd'
                    _hover={{ background: '#dddddd55' }}
                  >
                    {row.cells.map((cell, index) => {
                      let data: ReactNode;
                      if (cell.column.Header === 'NAME') {
                        data = (
                          <HStack key={index}>
                            <VStack align='start' spacing={1}>
                              <Text
                                color={nameColor}
                                textDecoration="underline"
                                textUnderlineOffset={2}
                                fontSize="sm"
                                fontWeight="700"
                              >
                                {cell.row.original.firstName} {cell.row.original.lastName}
                              </Text>
                              <Text>{cell.row.original.email}</Text>
                            </VStack>
                          </HStack>
                        )
                      } else if (cell.column.Header === 'RELATION') {
                        data = (
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="600"
                          >
                            <Badge ml='2' variant='outline'>
                              {cell.row.original?.relationType}
                            </Badge>
                          </Text>
                        );
                      } else if (cell.column.Header === 'SOURCE') {
                        data = (
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Created by <Badge ml='2' colorScheme={getSourceColor(cell.row.original?.source)} variant='outline'>
                            {cell.row.original?.source}
                          </Badge>
                          </Text>
                        );
                      } else if (cell.column.Header === 'STATUS') {
                        const status = cell.row.original.accepted === true ? 'COMPLETED' : 'INACTIVE';
                        data = (
                          <HStack>
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="700"
                            >
                              <Badge
                                colorScheme={status ? getBadgeColor(status) : 'red'}>
                                {cell.row.original.accepted === true ? 'ACCEPTED' : 'NOT ACCEPTED'}
                              </Badge>
                            </Text>
                          </HStack>
                        )
                      } else if (cell.column.Header === 'VIEW DATA') {
                        data = (
                          <HStack>
                            <Popover
                              placement="auto-start"
                              isOpen={openedPopoverId === row.original.userId}
                            >
                              <PopoverTrigger>
                                <Button
                                  rightIcon={<MdSend/>}
                                  size="sm"
                                  fontSize="sm"
                                  colorScheme="blue"
                                  fontWeight="500"
                                  minW='162px'
                                  onClick={() => {
                                    setOpenedPopoverId(row.original.userId);
                                    setSelectedMemberData(row.original);
                                  }}
                                  disabled={user.inviteType === 'observer'}
                                >
                                  Invite
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent w="auto">
                                <PopoverHeader fontWeight="semibold" pr={8} maxW='240px'>
                                  Are you sure you want to
                                  invite {cell.row.original.firstName} {cell.row.original.lastName}?
                                </PopoverHeader>
                                <PopoverArrow/>
                                <PopoverCloseButton onClick={() => {
                                  setOpenedPopoverId(null);
                                }}/>
                                <PopoverBody maxW='240px'>
                                  <FormLabel>Invite Type</FormLabel>
                                  <RadioGroup
                                    value={connectType}
                                    onChange={(e: 'CONNECT' | 'UPLOAD') => setConnectType(e)}
                                  >
                                    <HStack>
                                      <Radio
                                        colorScheme="brandScheme"
                                        fontSize='sm'
                                        value='CONNECT'>
                                        CONNECT
                                      </Radio>
                                      <Radio
                                        colorScheme="brandScheme"
                                        fontSize='sm'
                                        value='UPLOAD'
                                      >
                                        UPLOAD
                                      </Radio>
                                    </HStack>
                                    <HStack w='full' justify='end'>
                                      <Button
                                        mt={2}
                                        size='sm'
                                        colorScheme='brand'
                                        onClick={() => {
                                          setOpenedPopoverId(null);
                                          resendInvitationEmail(cell.row.original.userId, connectType);
                                        }}
                                      >
                                        Send
                                      </Button>
                                    </HStack>
                                  </RadioGroup>
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          </HStack>
                        )
                      } else {
                        data = (
                          <HStack>
                            <Tooltip hasArrow label='Reset password'>
                              <IconButton
                                aria-label="Reset password"
                                bg="inherit"
                                ms="auto"
                                borderRadius="inherit"
                                onClick={() => {
                                  onResetOpen();
                                  setSelectedMemberData(row.original);
                                }}
                                _active={{
                                  bg: 'inherit',
                                  transform: 'none',
                                  borderColor: 'transparent',
                                }}
                                _focus={{ boxShadow: 'none' }}
                                icon={ <MdLoop/> }
                                disabled={user.inviteType === 'observer'}
                              />
                            </Tooltip>
                            <Popover placement="auto-start">
                              {({ onClose }) => (
                                <>
                                  <PopoverTrigger>
                                    <IconButton
                                      aria-label="Remove member"
                                      bg="inherit"
                                      ms="auto"
                                      borderRadius="inherit"
                                      _active={{
                                        bg: 'inherit',
                                        transform: 'none',
                                        borderColor: 'transparent',
                                      }}
                                      _focus={{ boxShadow: 'none' }}
                                      icon={ <MdPersonRemove color="secondaryGray.500"/> }
                                      disabled={user.inviteType === 'observer'}
                                    />
                                  </PopoverTrigger>
                                  <PopoverContent w="auto" minW="280px">
                                    <PopoverHeader fontWeight="semibold">Remove Member</PopoverHeader>
                                    <PopoverArrow/>
                                    <PopoverCloseButton/>
                                    <PopoverBody>
                                      <Box display="flex" marginBottom="8px">
                                        <WarningIcon color="orange" marginRight="4px" marginTop="4px"/>
                                        <Box>
                                          Are you sure to remove <b>{cell.row.original.firstName} {cell.row.original.lastName}</b>?
                                        </Box>
                                      </Box>
                                      <Box display="flex" justifyContent="flex-end">
                                        <Button
                                          size="xs"
                                          margin="4px"
                                          width="50px"
                                          onClick={onClose}
                                        >
                                          No
                                        </Button>
                                        <Button
                                          isLoading={isDeleting}
                                          isDisabled={isDeleting}
                                          size="xs"
                                          margin="4px"
                                          width="50px"
                                          colorScheme="red"
                                          onClick={() => {
                                            removeMember(row.original.userId);
                                            onClose();
                                          }}>
                                            Yes
                                          </Button>
                                      </Box>
                                    </PopoverBody>
                                  </PopoverContent>
                                </>
                              )}
                            </Popover>
                          </HStack>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          fontSize={{ sm: '14px' }}
                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                          borderColor="transparent"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
          <Pagination
            pagesCount={pagesCount}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          >
            <PaginationContainer
              align="center"
              justify="center"
              p={4}
              gap={2}
              w="full"
            >
              <PaginationPrevious
                _hover={{
                  bg: mode('brand.600', 'brand.400'),
                }}
                bg={bgPagination}
              > Prev
              </PaginationPrevious>
              <PaginationPageGroup
                isInline
                align="center"
                separator={
                  <PaginationSeparator
                    bg="transparent"
                    borderColor="brand.100"
                    borderWidth="1px"
                    fontSize="sm"
                    w={10}
                    jumpSize={5}
                  />
                }
              >
                {pages.map((page) => (
                  <PaginationPage
                    key={`pagination_page_${page}`}
                    page={page}
                    w={10}
                    bg="transparent"
                    borderColor="brand.100"
                    borderWidth="1px"
                    fontSize="sm"
                    _hover={{
                      bg: 'brand.600'
                    }}
                    _current={{
                      bg: 'brand.600',
                      fontSize: 'sm',
                      w: 10
                    }}
                  />
                ))}
              </PaginationPageGroup>
              <PaginationNext
                _hover={{
                  bg: mode('brand.600', 'brand.400'),
                }}
                bg={bgPagination}
              >Next</PaginationNext>
            </PaginationContainer>
          </Pagination>
        </Card>
      </VStack>
      <AddMemberModal isOpen={isOpen} onClose={onClose} onSave={addNewMember}/>
      <ResetPasswordModal
        isOpen={isResetOpen}
        onClose={onResetClose}
        onReset={resetPassword}
        user={selectedMemberData}
      />
    </Box>
  );
};

export const getSourceColor = (source?: 'internal' | 'smartleads' | 'Wealthbox' | 'Redtail' | 'API' | 'Salesforce'): string => {
  switch (source) {
    case 'internal':
      return 'green';
    case 'smartleads':
      return 'orange';
    case 'Wealthbox':
      return 'navy';
    case 'Redtail':
      return 'red';
    case 'API':
      return 'purple';
    case 'Salesforce':
      return 'blue';
    default:
      return 'red';
  }
};

export default MemberList;
